import { createContext, useContext, useState } from "react";
import { useInitiatePayment } from "../hooks/useInitiatePayment";
import { Card, Flex, Image, Modal, Space, Spin, Typography } from "antd";
import { Button } from "../custom";

interface PaymentContextType {
  initiatePayment: (data: ProcessPaymentPayloadWithChannel) => void;
  isProcessing: boolean;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error("usePayment must be used within a PaymentProvider");
  }
  return context;
};

interface PaymentProviderProps {
  children: React.ReactNode;
}

export const PaymentProvider: React.FC<PaymentProviderProps> = ({
  children,
}) => {
  const { makePayment, isLoading, paymentChannels, isLoadingChannels } =
    useInitiatePayment();
  const { Text } = Typography;

  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<string>("Cyberpay");
  const [currentPaymentData, setCurrentPaymentData] =
    useState<ProcessPaymentPayloadWithChannel | null>(null);

  const initiatePayment = (data: ProcessPaymentPayloadWithChannel) => {
    setCurrentPaymentData(data);
    setChannelDialogOpen(true);
  };

  const handleConfirmChannel = async () => {
    if (!currentPaymentData) return;
    await makePayment({
      ...currentPaymentData,
      channel: selectedChannel,
    });
    setChannelDialogOpen(false);
    setCurrentPaymentData(null);
  };

  const contextValue: PaymentContextType = {
    initiatePayment,
    isProcessing: isLoading,
  };

  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
      <Modal
        open={channelDialogOpen}
        centered
        title="Select Payment Channel"
        footer={null}
        onCancel={() => setChannelDialogOpen(false)}
      >
        {isLoadingChannels ? (
          <Spin />
        ) : (
          <div>
            {paymentChannels?.map((channel) => (
              <Card
                key={channel.name}
                hoverable
                onClick={() => setSelectedChannel(channel.name)}
                style={{
                  marginBottom: 12,
                  border: "2px solid",
                  borderColor:
                    selectedChannel === channel.name
                      ? "var(--color-primary)"
                      : "var(--color-border)",
                  borderRadius: 8,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                onMouseEnter={(e) => {
                  if (selectedChannel !== channel.name) {
                    e.currentTarget.style.borderColor = "var(--color-primary)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedChannel !== channel.name) {
                    e.currentTarget.style.borderColor = "var(--color-border)";
                  }
                }}
                styles={{ body: { padding: 12, width: "100%" } }}
              >
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {channel.imageUrl && (
                    <Image
                      src={channel.imageUrl}
                      alt={channel.name}
                      height={40}
                      preview={false}
                      style={{
                        width: "auto",
                        maxWidth: 150,
                        objectFit: "contain",
                      }}
                    />
                  )}
                  <Text style={{ fontWeight: 600, fontSize: "24px" }}>
                    {channel.name === "Cyperpay" ? "CyberPay" : channel.name}
                  </Text>
                </div>
              </Card>
            ))}
            <Flex justify="flex-end" gap={".75rem"}>
              <Button
                onClick={() => setChannelDialogOpen(false)}
                text="Cancel"
              />
              <Button
                text="Continue"
                onClick={handleConfirmChannel}
                isLoading={isLoading}
                disabled={!selectedChannel || isLoading}
              />
            </Flex>
          </div>
        )}
      </Modal>
    </PaymentContext.Provider>
  );
};
